import { createFileRoute } from '@tanstack/react-router';
import { Box, Group, LoadingOverlay, Pagination, Stack, Table, Text } from '@mantine/core';
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListSystemDto, systemSearchSchema } from '@features/systems/domain/systems.types';
import { systemListQueryOptions } from '@api/systems/systemQueries';
import { PageHeader, StatusBadge } from '@components/ui';
import { formatDistanceToNow } from 'date-fns';
import { formatDateTime } from '@utils/dateFormatters';
import { ProtectedComponent } from '@components/ProtectedComponent';
import { FallbackType, UserRole } from '@shared-types/auth.types';
import { IconRefresh } from '@tabler/icons-react';
import { SystemConnectionsFilter } from '@features/systems/components/SystemConnectionsFilter';

export const Route = createFileRoute('/_authed/admin/systems/')({
  component: SystemManagementSection,
  validateSearch: systemSearchSchema,
  loaderDeps: ({search: {page, pageSize, isOnline, searchTerm}}) => ({
    page,
    pageSize,
    isOnline,
    searchTerm,
  }),
  loader: ({context: {queryClient}, deps}) => {
    queryClient.prefetchQuery(systemListQueryOptions(deps));
  },
});

function SystemManagementSection() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const {data, isLoading} = useQuery(systemListQueryOptions(search));

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({queryKey: ['systems']});
  }, [queryClient]);

  const handlePageChange = (page: number) => {
    navigate({search: (prev) => ({...prev, page})});
  };

  const handleRowClick = (system: ListSystemDto) => {
    navigate({
      to: '/systems/$systemId',
      params: {systemId: system.id},
    });
  };

  const systems = data?.systems ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / search.pageSize));

  return (
    <ProtectedComponent
      requiredRole={UserRole.Viewer}
      fallbackType={FallbackType.Section}
      fallbackTitle="Systems"
      fallbackAction="view systems"
    >
      <Box>
        <PageHeader
          title="Systems"
          subtitle="Manage and monitor all systems"
          backButton={false}
          actions={[
            {
              label: 'Refresh',
              icon: IconRefresh,
              onClick: handleRefresh,
              loading: isLoading,
              ariaLabel: 'refresh-systems',
              color: 'green',
            },
          ]}
        >
          <Group justify="space-between" mt="md">
            <SystemConnectionsFilter search={search}/>
            <Text size="sm" c="dimmed">
              Showing {systems.length} of {totalCount} systems
            </Text>
          </Group>
        </PageHeader>

        <Box mt="lg" pos="relative">
          <Table.ScrollContainer minWidth={700} h="69vh" type="scrollarea">
            <Table verticalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>System ID</Table.Th>
                  <Table.Th>Machine Name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Registered</Table.Th>
                  <Table.Th>Status Changed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {systems.map((system) => (
                  <Table.Tr
                    key={system.id}
                    style={{cursor: 'pointer'}}
                    onClick={() => handleRowClick(system)}
                  >
                    <Table.Td>
                      <Text ff="monospace" size="sm" c="primary">{system.id}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{system.machineName || 'N/A'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={system.isOnline ? 'online' : 'offline'}/>
                    </Table.Td>
                    <Table.Td>{formatDateTime(system.registeredAt)}</Table.Td>
                    <Table.Td>{formatDistanceToNow(new Date(system.isOnlineChangedAt))} ago</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {!isLoading && systems.length === 0 && (
              <Stack align="center" gap="md" py={60}>
                <Text size="lg" fw={500}>No Systems Found</Text>
                <Text size="sm" c="dimmed">No systems match your current criteria.</Text>
              </Stack>
            )}
          </Table.ScrollContainer>
          <LoadingOverlay visible={isLoading}/>
        </Box>

        {totalCount > 0 && totalPages > 1 && (
          <Group justify="center" mt="lg">
            <Pagination
              value={data?.page ?? 1}
              onChange={handlePageChange}
              total={totalPages}
              size="md"
              radius="md"
            />
          </Group>
        )}
      </Box>
    </ProtectedComponent>
  );
}
